import moment from "moment";
import React, { useEffect } from "react";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { Form } from "antd";
import { jwtDecode } from "jwt-decode";
import { pick } from "lodash";
import AccountAccumulators from "../../components/AccountAccumulators/AccountAccumulators";
import { checkIsLoaded, checkIsLoading } from "../../lib/utils/checkLoadingStatus";
import { displayFormat, toDisplayFormat, toHttpFormat } from "../../lib/utils/formatDate";
import versionsTableUtils from "../../lib/utils/versionsTableUtils";
import { fetchGetAgreementsByAccId } from "../../store/slices/agreements";
import {
  fetchServiceStatus,
  fetchServiceUsageDocumentType,
  fetchServiceUsageDirection,
  fetchTariffModelTypes,
  fetchPeriodTypes,
  fetchRetailNetworks,
  fetchTariffPeriodAlignments,
} from "../../store/slices/rtkDictionaries";
import { fetchServiceByAggId } from "../../store/slices/services";
import {
  GetServiceTooltipTitle,
  GetAccumulators,
  HandleExportAccumulators,
  LoadServices,
} from "../../lib/interfaces/Accumulators";
import { cleanAccumulators, fetchAccumulators } from "../../store/slices/accumulators";
import AuthService from "../../services/AuthService";
import { exportToXLS } from "../../store/slices/xlsExport";
import { getAccountVersions } from "../../store/slices/accountsTs";
import { AccessToken } from "../../lib/types/AccessToken";

const AccountAccumulatorsContainer = () => {
  const dispatch = useDispatch();
  const { accountId } = useParams<{ accountId: string }>();
  const { agreements, agreementsLoading } = useSelector(
    (state: RootStateOrAny) => state.agreements
  );
  const {
    serviceStatusLoading,
    serviceStatus,
    serviceUsageDocumentTypeLoading,
    serviceUsageDirectionLoading,
    tariffPeriodAlignmentsLoading,
    tariffModelTypesLoading,
    periodTypesLoading,
    retailNetworksLoading,
  } = useSelector((state: RootStateOrAny) => state.rtkDictionaries);
  const { service, serviceLoading } = useSelector((state: RootStateOrAny) => state.service);
  const { accountVersions } = useSelector(
    (state: RootStateOrAny) => state.accountsTs.accountListRowData
  );
  const [searchForm] = Form.useForm();

  // Загрузка договоров и справочников
  useEffect(() => {
    if (!checkIsLoaded(agreementsLoading))
      // @ts-ignore
      dispatch(fetchGetAgreementsByAccId({ accId: Number(accountId) }));
    if (!checkIsLoaded(serviceStatusLoading)) dispatch(fetchServiceStatus());
    if (!checkIsLoaded(serviceUsageDocumentTypeLoading)) dispatch(fetchServiceUsageDocumentType());
    if (!checkIsLoaded(serviceUsageDirectionLoading)) dispatch(fetchServiceUsageDirection());
    if (!checkIsLoaded(tariffPeriodAlignmentsLoading)) dispatch(fetchTariffPeriodAlignments());
    if (!checkIsLoaded(tariffModelTypesLoading)) dispatch(fetchTariffModelTypes());
    if (!checkIsLoaded(periodTypesLoading)) dispatch(fetchPeriodTypes());
    if (!checkIsLoaded(retailNetworksLoading)) dispatch(fetchRetailNetworks());
    const loadedAccountVersionId = accountVersions[0]?.id;
    if (Number(loadedAccountVersionId) !== Number(accountId)) {
      dispatch(getAccountVersions({ accountId: Number(accountId) }));
    }
  }, []);

  // Очистка аккумуляторов по выходу
  useEffect(
    () => () => {
      dispatch(cleanAccumulators());
    },
    []
  );

  const getServiceTooltipTitle: GetServiceTooltipTitle = (item) => {
    const cellStyle = {
      border: `1px solid rgba(0, 0, 0, 0.1)`,
      padding: "5px",
      color: "rgba(0, 0, 0, 0.3)",
      backgroundColor: "white",
    };
    return (
      <div style={{ display: "flex" }}>
        <div style={cellStyle}>{item.serviceType.name}</div>
        {item.offering?.extName ? <div style={cellStyle}>{item.offering?.extName}</div> : null}
        <div style={cellStyle}>
          {versionsTableUtils.geDicValueById(serviceStatus, item.serviceStatusId)?.name}
        </div>
        <div style={cellStyle}>{moment(item.fromDate).format(displayFormat)}</div>
        <div style={cellStyle}>{moment(item.toDate).format(displayFormat)}</div>
        <div style={cellStyle}>{item.id}</div>
      </div>
    );
  };

  // Загрузка продуктов: если договор выбран - по выбранному договору, иначе - по всем договорам
  const loadServices: LoadServices = ({ onDate, agreementId }) => {
    const agreementsIds =
      agreementId && agreementId > 0 ? [agreementId] : agreements.map(({ id }) => id);
    if (agreementsIds && !checkIsLoading(serviceLoading)) {
      const bodyRequest = {
        agreementsIds,
        futureDates: false,
        includePastDates: false,
        onDate: toHttpFormat(onDate),
      };
      // @ts-ignore
      dispatch(fetchServiceByAggId(bodyRequest));
    }
  };

  // Загрузка продуктов
  useEffect(() => {
    if (checkIsLoaded(agreementsLoading)) {
      loadServices({ onDate: moment() });
    }
  }, [agreementsLoading]);

  const headerList = [
    { name: "serviceId", title: "ID продукта", type: "Double" },
    { name: "serviceTypeName", title: "Продукт", type: "String" },
    { name: "tariffId", title: "ID тарифа", type: "Double" },
    { name: "tariffName", title: "Тариф", type: "String" },
    { name: "tariffModelName", title: "Бизнес-модель", type: "String" },
    { name: "periodTypeName", title: "Период тарифа", type: "String" },
    { name: "periodAlignmentName", title: "Смещение периода", type: "String" },
    { name: "servicePeriod", title: "Период действия", type: "String" },
    { name: "serviceAggRuleId", title: "ID группы", type: "Double" },
    { name: "aggName", title: "Название группы", type: "String" },
    { name: "rangeFrom", title: "От (шт)", type: "Double" },
    { name: "rangeTo", title: "До (шт)", type: "Double" },
    { name: "includeLists", title: "Сеть", type: "String" },
    { name: "documentTypeLists", title: "Тип документа", type: "String" },
    { name: "directions", title: "Направление", type: "String" },
    { name: "id", title: "ID аккум.", type: "Double" },
    { name: "accumulationPeriod", title: "Период накопления", type: "String" },
    { name: "eventsCount", title: "Кол-во", type: "Double" },
  ];

  // Выгрузка в XLS
  const handleExport: HandleExportAccumulators = ({ toDate, data, columns }) => {
    const token = AuthService.getCurrentUser();
    const author = token ? jwtDecode<AccessToken>(token).display_name : undefined;
    const header = headerList.filter(({ name }) => {
      const column = columns.find(({ dataIndex }) => dataIndex === name);
      return !column?.hidden;
    });

    const headerNames: string[] = header.map(({ name }) => name);

    const dataForExport = data.map((item) => {
      const servicePeriod = `${item.servicePeriod.fromDate} - ${item.servicePeriod.toDate}`;
      const accumulationPeriod = `${item.accumulationPeriod.periodFrom} - ${item.accumulationPeriod.periodTo}`;
      const includeLists = item.includeLists
        .map(({ nameTitle }) => nameTitle.replace(/\n/g, "/"))
        .join(", ");
      const documentTypeLists = item.documentTypeLists
        .map(({ nameTitle }) => nameTitle.replace(/\n/g, "/"))
        .join(", ");
      const directions = item.directions.join(", ");
      return pick(
        { ...item, servicePeriod, accumulationPeriod, includeLists, documentTypeLists, directions },
        headerNames
      );
    });

    const exportedData = {
      fileName: `Аккумуляторы аккаунта_${toDisplayFormat(moment())}`,
      title: "Выгрузка данных в формате xls",
      description: "Выгрузка данных",
      periodTo: moment(toDate).format("YYYY-MM-DD"),
      author,
      header,
      data: dataForExport,
    };
    // @ts-ignore
    dispatch(exportToXLS(exportedData));
  };

  const getAccumulators: GetAccumulators = ({ serviceId, onDate }) => {
    const serviceIds =
      serviceId && serviceId > 0
        ? [serviceId]
        : service.filter(({ serviceType }) => serviceType.serviceModeId === 3).map(({ id }) => id);
    if (serviceIds.length > 0) {
      dispatch(
        fetchAccumulators({
          serviceIds,
          onDate,
        })
      );
    } else {
      dispatch(cleanAccumulators());
    }
  };

  return (
    <div className="container_height">
      <AccountAccumulators
        getServiceTooltipTitle={getServiceTooltipTitle}
        loadServices={loadServices}
        searchForm={searchForm}
        getAccumulators={getAccumulators}
        handleExport={handleExport}
      />
    </div>
  );
};

export default AccountAccumulatorsContainer;
